"use client"
import React, { useEffect, useRef, useState } from 'react'
import './style.css'
import { Divider, Input, Spin, Modal, Typography } from 'antd';
import { formatMsg, Helper, notification, useIsMobile } from '../..';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Download from "yet-another-react-lightbox/plugins/download";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/plugins/counter.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Video from "yet-another-react-lightbox/plugins/video";
import Inline from "yet-another-react-lightbox/plugins/inline";
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import { useDispatch } from 'react-redux';
import {
    getActivityComment,
    likeActivityComment,
    postActivityComment,
    postCommentReply,
    updateActivityComment,
    deleteActivityComment,
    updateCommentReply,
    deleteCommentReply
} from '../../redux/activity/reducer';

function useKeyboardPadding() {
    const [keyboardPadding, setKeyboardPadding] = useState(0);

    useEffect(() => {
        const handler = () => {
            const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
            const docHeight = window.innerHeight;
            const diff = docHeight - viewportHeight;
            setKeyboardPadding(diff > 0 ? diff : 0);
        };
        window.visualViewport?.addEventListener("resize", handler);
        window.addEventListener("resize", handler);

        handler();

        return () => {
            window.visualViewport?.removeEventListener("resize", handler);
            window.removeEventListener("resize", handler);
        };
    }, []);
    return keyboardPadding;
}

const ActivityCommentsPopup = ({ liked, likeList, showActivityLikeCommentsModal, setShowActivityLikeCommentsModal, view, activityObj, handleLike, likesCount }) => {
    const { firebase, activityReducer } = Helper.getSelectorState(["activityReducer"]);
    const dispatch = useDispatch();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [comment, setComment] = useState<string | undefined>(undefined);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const [replyToComment, setReplyToComment] = useState<any>(null);
    const [replyText, setReplyText] = useState('');
    const [showRepliesFor, setShowRepliesFor] = useState<Record<string, boolean>>({});
    const [replyEmojiPicker, setReplyEmojiPicker] = useState<string | null>(null);

    const [editingComment, setEditingComment] = useState<any>(null);
    const [editingCommentText, setEditingCommentText] = useState('');
    const [editingReply, setEditingReply] = useState<{ parentId: string, reply: any } | null>(null);
    const [editingReplyText, setEditingReplyText] = useState('');
    const [toggle, setToggle] = useState(false);

    const popupRef = useRef<any>(null);
    const inputRef = useRef<any>(null);
    const replyInputRef = useRef<any>(null);
    const editCommentInputRef = useRef<any>(null);
    const editReplyInputRef = useRef<any>(null);

    const isMobile = useIsMobile();
    const keyboardPadding = useKeyboardPadding();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const modalRoot = document.querySelector(".ant-modal-root");
            if (modalRoot && modalRoot.contains(event.target as Node)) return;

            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setShowActivityLikeCommentsModal(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                const modalRoot = document.querySelector(".ant-modal-root");
                if (modalRoot) return;

                setShowActivityLikeCommentsModal(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        if (showActivityLikeCommentsModal === "comment" && inputRef.current) inputRef.current.focus();
        if (showActivityLikeCommentsModal && view === "COMMENTS") {
            dispatch(getActivityComment({ firebase, payload: { activityPostId: activityObj.id } }))
        }
        setToggle(prev => !prev);
    }, [showActivityLikeCommentsModal, view]);

    useEffect(() => {
        if (activityReducer.refetchComments) dispatch(getActivityComment({ firebase, payload: { activityPostId: activityObj.id } }))
    }, [activityReducer.refetchComments]);

    useEffect(() => {
        if (editingComment && editCommentInputRef.current) editCommentInputRef.current.focus();
    }, [editingComment]);

    useEffect(() => {
        if (editingReply && editReplyInputRef.current) editReplyInputRef.current.focus();
    }, [editingReply]);

    useEffect(() => {
        if (replyToComment && replyInputRef.current) replyInputRef.current.focus();
    }, [replyToComment]);

    const toggleOpen = (state: boolean) => () => setIsPreviewOpen(state);
    const updateIndex = ({ index: current }: { index: number }) => setCurrentIndex(current);

    const handleEmojiSelect = (emoji: any) => setComment(prev => (prev || "") + emoji.native);

    const handleSubmitComment = (commentText: string = "") => {
        commentText = commentText.trim();
        if (!commentText) return notification("error", formatMsg("error.addComment"));
        dispatch(postActivityComment({ firebase, payload: { activityPostId: activityObj.id, text: commentText } }))
    }

    const handleCommentLike = (commentId: string) => {
        dispatch(likeActivityComment({
            firebase,
            type: "comment",
            reqObj: {
                userId: firebase?.user?.id,
                userType: firebase?.user?.userType,
                activityPostId: activityObj.id,
                commentId,
                userName: firebase.user.name,
                userProfilePicture: firebase.user.profilePhoto || firebase.user.profileImageUrl || firebase.user.authorProfileUrl || null
            }
        }))
    }

    const handleReplyLike = (commentId: string, replyId: string) => {
        dispatch(likeActivityComment({
            firebase,
            type: "reply",
            reqObj: {
                userId: firebase?.user?.id,
                userType: firebase?.user?.userType,
                activityPostId: activityObj.id,
                commentId,
                replyId,
                userName: firebase.user.name,
                userProfilePicture: firebase.user.profilePhoto || firebase.user.profileImageUrl || firebase.user.authorProfileUrl || null
            }
        }))
    }

    const handleReplyEmojiSelect = (emoji: any) => setReplyText(prev => (prev || "") + emoji.native);

    const handleReply = (comment: any) => {
        setReplyToComment(comment);
        setReplyText('');
    }

    const handleCancelReply = () => {
        setReplyToComment(null);
        setReplyText('');
    }

    const handleSubmitReply = (e) => {
        if (!e.shiftKey) {
            e.preventDefault();
            if (!replyText.trim()) return notification("error", formatMsg("error.addReply"));
            dispatch(postCommentReply({
                firebase,
                payload: {
                    activityPostId: activityObj.id,
                    commentId: replyToComment.id,
                    text: replyText.trim(),
                }
            }));
            setShowRepliesFor(prev => ({ ...prev, [replyToComment.id]: true }));
            setReplyText('');
            setReplyToComment(null);
        }
    }

    const toggleShowReplies = (commentId: string) => setShowRepliesFor(prev => ({ ...prev, [commentId]: !prev[commentId] }));

    const startEditComment = (comment: any) => {
        setEditingComment(comment);
        setEditingCommentText(comment.text);
    }

    const cancelEditComment = () => {
        setEditingComment(null);
        setEditingCommentText('');
    }

    const saveEditComment = () => {
        if (!editingCommentText.trim()) return notification("error", formatMsg("error.addComment"));
        dispatch(updateActivityComment({
            firebase,
            payload: {
                activityPostId: activityObj.id,
                commentId: editingComment.id,
                text: editingCommentText.trim()
            }
        }))
        cancelEditComment();
    }

    const confirmDeleteComment = (commentId: string) => {
        Modal.confirm({
            title: formatMsg("confirm.deleteComment"),
            okText: formatMsg("delete"),
            cancelText: formatMsg("cancel"),
            onOk: () => {
                dispatch(deleteActivityComment({ firebase, payload: { activityPostId: activityObj.id, commentId } }))
                Modal.destroyAll()
            }
        })
    }

    const startEditReply = (parentId: string, reply: any) => {
        setEditingReply({ parentId, reply });
        setEditingReplyText(reply.text);
    }

    const cancelEditReply = () => {
        setEditingReply(null);
        setEditingReplyText('');
    }

    const saveEditReply = () => {
        if (!editingReplyText.trim()) return notification("error", formatMsg("error.addReply"));
        dispatch(updateCommentReply({
            firebase,
            payload: {
                activityPostId: activityObj.id,
                commentId: editingReply.parentId,
                replyId: editingReply.reply.id,
                text: editingReplyText.trim()
            }
        }))
        cancelEditReply();
    }

    const confirmDeleteReply = (commentId: string, replyId: string) => {
        Modal.confirm({
            title: formatMsg("confirm.deleteReply"),
            okText: formatMsg("delete"),
            cancelText: formatMsg("cancel"),
            onOk: () => {
                dispatch(deleteCommentReply({ firebase, payload: { activityPostId: activityObj.id, commentId, replyId } }))
                Modal.destroyAll()
            }
        })
    }

    const renderCommentFooter = (comment: any) => (
        <>
            <div className='activity-comment-footer'>{Helper.timeAgo(comment.timestamp)}</div>
            <div className='activity-comment-footer'>{comment.likes?.length || 0} like{comment.likes?.length !== 1 ? 's' : ''}</div>
            <div className='activity-comment-footer activity-reply-btn' onClick={() => replyToComment?.id === comment.id ? handleCancelReply() : handleReply(comment)}>
                {formatMsg("reply")}
            </div>
            {firebase.user?.id === comment.userId && (
                <div className='activity-comment-footer activity-reply-btn' onClick={() => editingComment?.id === comment.id ? cancelEditComment() : startEditComment(comment)}>
                    {formatMsg("edit")}
                </div>
            )}
            {(firebase.user?.id === comment.userId || firebase?.teacher?.superUser || (comment.userType === "ACCOUNT" && activityObj.taggedUsers?.map(t => t.userId)?.includes(firebase?.teacher?.id))) && (
                <div className='activity-comment-footer activity-reply-btn' onClick={() => confirmDeleteComment(comment.id)}>
                    {formatMsg("delete")}
                </div>
            )}
        </>
    );

    const renderReplyFooter = (commentId: string, reply: any) => (
        <>
            <div className='activity-comment-footer'>{Helper.timeAgo(reply.timestamp)}</div>
            <div className='activity-comment-footer'>{reply.likes?.length || 0} like{reply.likes?.length !== 1 ? 's' : ''}</div>
            {firebase.user?.id === reply.userId && (
                <div className='activity-comment-footer activity-reply-btn' onClick={() => editingReply?.reply.id === reply.id ? cancelEditReply() : startEditReply(commentId, reply)}>
                    {formatMsg("edit")}
                </div>
            )}
            {(firebase.user?.id === reply.userId || firebase?.teacher?.superUser || (reply.userType === "ACCOUNT" && activityObj.taggedUsers?.map(t => t.userId)?.includes(firebase?.teacher?.id))) && (
                <div className='activity-comment-footer activity-reply-btn' onClick={() => confirmDeleteReply(commentId, reply.id)}>
                    {formatMsg("delete")}
                </div>
            )}
        </>
    );

    const renderInputBox = (
        placeholder: string,
        value: string | undefined,
        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
        cusRef: React.RefObject<HTMLTextAreaElement>,
        onPressEnter: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void,
        onSubmit: (e?) => void,
        onEmojiClick?: () => void,
        onCancel?: () => void
    ) => {
        const leftPad = onEmojiClick ? 40 : 16;
        const rightPad = onCancel ? 84 : 40;

        return (
            <div style={{ position: "relative", width: "100%" }}>
                {onEmojiClick &&
                    <button
                        type="button"
                        onClick={onEmojiClick}
                        style={{
                            position: "absolute",
                            left: 14,
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            zIndex: 1,
                            height: 20,
                        }}
                    >
                        <img
                            src="/images/smileEmoji.png"
                            className="activity-comments-icon"
                            alt="smile-icon"
                        />
                    </button>
                }

                <Input.TextArea
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    ref={cusRef}
                    onPressEnter={onPressEnter}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    style={{
                        width: "100%",
                        padding: isMobile ? `8px ${rightPad}px 8px ${leftPad}px` : `12px ${rightPad}px 12px ${leftPad}px`,
                        border: "1px solid #8D8D8D",
                        backgroundColor: "#F8F8F8",
                        borderRadius: "32px",
                        resize: "none",
                        height: isMobile ? 38 : 48,
                        minHeight: isMobile ? 38 : 48,
                        overflowY: value ? "auto" : "hidden",
                    }}
                    className="activity-addComments"
                />

                <div
                    style={{
                        position: "absolute",
                        right: 14,
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="activity-reply-cancel-btn"
                            style={{
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                            }}
                        >
                            {formatMsg("cancel")}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onSubmit}
                        style={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            height: 20,
                        }}
                    >
                        <img
                            src="/images/commentsArrow.png"
                            className="activity-comments-icon"
                            alt="comments-arrow"
                        />
                    </button>
                </div>
            </div>
        )
    };

    return (
        <div>
            <div className={isMobile ? "activity-bottom-sheet-overlay" : "activity-overlay"}>
                <div
                    className={isMobile ? "activity-bottom-sheet" : "activity-popup"}
                    ref={popupRef}
                    style={
                        isMobile
                            ? { paddingBottom: 16 + keyboardPadding }
                            : { width: activityObj.mediaProfiles ? "80%" : "40%" }
                    }
                >
                    <div className="activity-header">
                        <div className="activity-header-actions">
                            <img src="/images/leftArrow.png" className='activity-arrow-icon' onClick={() => setShowActivityLikeCommentsModal(false)} alt="left-arrow" />
                            <div className='activity-user-info'>
                                <img className='activity-profileIconStyle' src={Helper.getProfileImageUrl(activityObj)} />
                                <div className="activity-userDetails">
                                    <div className="activity-user-name">{activityObj?.authorName || formatMsg("alexander")}</div>
                                    <div className="activity-timestamp-container">
                                        <div className="activity-timestamp">{Helper.timeAgo(activityObj.createdOn)}</div>
                                        <div className='activity-timestamp' style={{ marginLeft: 2, marginRight: 2 }}>•</div>
                                        <img src={`/images/${Helper.getVisibilityIcon(activityObj.visibility).icon}`} alt="globe" className="activity-globe-icon" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="activity-header-actions">{Helper.renderActivityType(activityObj.activityType, activityReducer.activityType, isMobile)}</div>
                    </div>

                    <div className='activity-divider'></div>

                    <div className="activity-main-container">
                        {!isMobile && activityObj.mediaProfiles && (
                            <div className="activity-left-container">
                                <Lightbox
                                    index={currentIndex}
                                    slides={activityObj.mediaProfiles.map(item => item.type.includes("video")
                                        ? ({ type: "video", sources: [{ src: item.path || item.url || item.preview, type: "video/mp4" }] } as const)
                                        : ({ src: item.path || item.url || item.preview, type: "image" } as const)
                                    )}
                                    video={{ autoPlay: true, playsInline: true, muted: true }}
                                    plugins={[Inline, Video]}
                                    on={{ view: updateIndex, click: toggleOpen(true) }}
                                    carousel={{ padding: 0, spacing: 0, imageFit: "cover" }}
                                    inline={{
                                        style: { width: "100%", maxWidth: "900px", aspectRatio: "3 / 2", margin: "0 auto", borderRadius: "16px" },
                                    }}
                                    render={{
                                        buttonPrev: activityObj.mediaProfiles.length === 1 ? () => null : undefined,
                                        buttonNext: activityObj.mediaProfiles.length === 1 ? () => null : undefined,
                                    }}
                                />

                                <Lightbox
                                    open={isPreviewOpen}
                                    index={currentIndex}
                                    close={() => setIsPreviewOpen(false)}
                                    slides={activityObj.mediaProfiles.map(item => item.type.includes("video")
                                        ? ({ type: "video", sources: [{ src: item.path || item.url || item.preview, type: "video/mp4" }] } as const)
                                        : ({ src: item.path || item.url || item.preview, type: "image" } as const)
                                    )}
                                    plugins={[Download, Counter, Fullscreen, Thumbnails, Zoom, Video]}
                                    video={{ autoPlay: true, playsInline: true, preload: "auto", muted: true }}
                                    counter={{ container: { style: { top: "unset", bottom: 0, left: "50%", transform: "translateX(-50%)" } } }}
                                    controller={{ closeOnBackdropClick: true, closeOnPullUp: true }}
                                />
                            </div>
                        )}

                        <div className='activity-comments-container'>

                            {view === "COMMENTS" && (
                                <Spin spinning={activityReducer.commentLoading}>
                                    <div className="activity-comments-section">
                                        <div className='activity-comments-title'>{formatMsg("comments")}</div>
                                        <div className='activity-comments-input'>
                                            <div style={{ position: 'relative' }}>
                                                {showEmojiPicker && (
                                                    <div style={{ position: 'absolute', top: '48px', left: '0', zIndex: 1000 }}>
                                                        <Picker data={emojiData} onEmojiSelect={handleEmojiSelect} theme="dark" onClickOutside={() => setShowEmojiPicker(false)} />
                                                    </div>
                                                )}
                                                {!activityObj.commentsOff && renderInputBox(
                                                    formatMsg("addComments"),
                                                    comment,
                                                    (e) => setComment(e.target.value),
                                                    inputRef,
                                                    (e) => {
                                                        if (!e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSubmitComment(comment!);
                                                            setComment(undefined);
                                                        }
                                                    },
                                                    () => {
                                                        handleSubmitComment(comment!);
                                                        setComment(undefined);
                                                    },
                                                    () => setShowEmojiPicker((p) => !p)
                                                )}
                                            </div>
                                        </div>

                                        <div className="activity-scrollable-container">
                                            {activityReducer.comments?.length ?
                                                (activityReducer.comments || []).map((comment: any, idx: number) => {
                                                    const user = Helper.getUserObjByUserType(comment.userId, comment.userType, firebase) || {};
                                                    const replyCount = comment.replies?.length || 0;

                                                    return (
                                                        <React.Fragment key={idx}>
                                                            <div className="activity-comment">
                                                                <div className='activity-comment-container'>
                                                                    <div className="activity-comment-profile">
                                                                        <img src={Helper.getProfileImageUrl(user, true, comment.userProfilePicture)} className='activity-comments-profile-icon' />
                                                                        <div className="activity-comment-user flex-center">{user?.name || comment.userName || "User"}</div>
                                                                    </div>
                                                                    {editingComment?.id === comment.id && !activityObj.commentsOff ? (
                                                                        renderInputBox(
                                                                            "",
                                                                            editingCommentText,
                                                                            (e) => setEditingCommentText(e.target.value),
                                                                            editCommentInputRef,
                                                                            (e) => {
                                                                                if (!e.shiftKey) {
                                                                                    e.preventDefault();
                                                                                    saveEditComment();
                                                                                }
                                                                            },
                                                                            saveEditComment,
                                                                            undefined,
                                                                            cancelEditComment
                                                                        )
                                                                    ) : <Typography.Paragraph className="activity-comment-text" ellipsis={{ rows: 2, expandable: "collapsible", symbol: e => e ? formatMsg("viewLess") : formatMsg("viewMore") }}>
                                                                        {comment.text}
                                                                    </Typography.Paragraph>
                                                                    }

                                                                    <div className="activity-comment-content">{renderCommentFooter(comment)}</div>
                                                                </div>
                                                                {comment.likes?.some(like => like.userId === firebase.user?.id) ? (
                                                                    <div className="activity-statItem-blank-heart-icon-2">
                                                                        <img src="/images/heart.png" className="activity-statItem-icon" alt="heart" onClick={() => handleCommentLike(comment.id)} />
                                                                    </div>
                                                                ) : (
                                                                    <div className="activity-statItem">
                                                                        <img src="/images/blank_heart.png" className="activity-statItem-blank-heart-icon-2" alt="heart" onClick={() => handleCommentLike(comment.id)} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {replyToComment && replyToComment.id === comment.id && (
                                                                <div className="activity-reply-input-container">
                                                                    <div style={{ position: 'relative', width: '100%' }}>
                                                                        {replyEmojiPicker === comment.id && (
                                                                            <div style={{ position: 'absolute', top: '48px', left: '0', zIndex: 1000 }}>
                                                                                <Picker data={emojiData} onEmojiSelect={handleReplyEmojiSelect} theme="dark" onClickOutside={() => setReplyEmojiPicker(null)} />
                                                                            </div>
                                                                        )}
                                                                        {!activityObj.commentsOff && renderInputBox(
                                                                            `${formatMsg("replyTo")} ${(user?.name || comment?.userName || "User") + "..."}`,
                                                                            replyText,
                                                                            (e) => setReplyText(e.target.value),
                                                                            replyInputRef,
                                                                            handleSubmitReply,
                                                                            handleSubmitReply,
                                                                            () => setReplyEmojiPicker(comment.id),
                                                                            handleCancelReply
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {replyCount > 0 && (
                                                                <div className="activity-show-replies-btn" onClick={() => toggleShowReplies(comment.id)}>
                                                                    {showRepliesFor[comment.id] ? `${formatMsg("hideReplies")} (${replyCount})` : `${formatMsg("showReplies")} (${replyCount})`}
                                                                </div>
                                                            )}
                                                            {showRepliesFor[comment.id] && comment.replies && comment.replies.length > 0 && (
                                                                <div className="activity-replies-container">
                                                                    {comment.replies.map((reply: any, replyIdx: number) => {
                                                                        const replyUser = Helper.getUserObjByUserType(reply.userId, reply.userType, firebase) || {};
                                                                        return (
                                                                            <div key={`reply-${replyIdx}`} className="activity-reply">
                                                                                <div className='activity-reply-content'>
                                                                                    <div className="activity-reply-profile">
                                                                                        <img src={Helper.getProfileImageUrl(replyUser, true, reply.userProfilePicture)} className='activity-comments-profile-icon' />
                                                                                        <div className="activity-comment-user flex-center">{replyUser?.name || reply.userName || "User"}</div>
                                                                                    </div>

                                                                                    {!activityObj.commentsOff && editingReply?.reply.id === reply.id ? (
                                                                                        renderInputBox(
                                                                                            "",
                                                                                            editingReplyText,
                                                                                            (e) => setEditingReplyText(e.target.value),
                                                                                            editReplyInputRef,
                                                                                            (e) => {
                                                                                                if (!e.shiftKey) {
                                                                                                    e.preventDefault();
                                                                                                    saveEditReply();
                                                                                                }
                                                                                            },
                                                                                            saveEditReply,
                                                                                            undefined,
                                                                                            cancelEditReply
                                                                                        )
                                                                                    ) :
                                                                                        <Typography.Paragraph className="activity-comment-text" ellipsis={{ rows: 2, expandable: "collapsible", symbol: e => e ? formatMsg("viewLess") : formatMsg("viewMore") }}>
                                                                                            {reply.text}
                                                                                        </Typography.Paragraph>
                                                                                    }

                                                                                    <div className="activity-comment-content">{renderReplyFooter(comment.id, reply)}</div>
                                                                                </div>
                                                                                {reply.likes?.some(like => like.userId === firebase.user?.id) ? (
                                                                                    <div className="activity-statItem-blank-heart-icon-2"><img src="/images/heart.png" className="activity-statItem-icon" alt="heart" onClick={() => handleReplyLike(comment.id, reply.id)} /></div>
                                                                                ) : (
                                                                                    <div className="activity-statItem"><img src="/images/blank_heart.png" className="activity-statItem-blank-heart-icon-2" alt="heart" onClick={() => handleReplyLike(comment.id, reply.id)} /></div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            <Divider style={{ margin: 0 }} />
                                                        </React.Fragment>
                                                    )
                                                }) :
                                                <div className="activity-no-comments">{formatMsg("noComments")}</div>
                                            }
                                        </div>
                                    </div>
                                </Spin>
                            )}

                            {view === "LIKES" && (
                                <div className="activity-comments-section">
                                    <div className='activity-comments-title'>{formatMsg("likes")}</div>
                                    <div className="activity-like-scrollable-container">
                                        {likeList?.map((like: any, idx: number) => {
                                            const user = Helper.getUserObjByUserType(like.userId, like.userType, firebase) || {};
                                            return (
                                                <div key={idx} className="activity-like-item">
                                                    <img src={Helper.getProfileImageUrl(user, true, like.userProfilePicture)} className='activity-user-logo' alt="user/customer_logo" />
                                                    <div className="activity-like-subitem ">
                                                        <div className="activity-comment-user">{user?.name || like.userName || "User"}</div>
                                                        <div className="activity-like-user-description">{formatMsg(like.userType)}</div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {!isMobile && <div className="activity-interactions">
                                <div className="activity-statItem">
                                    {liked ? (
                                        <img src="/images/heart.png" onClick={handleLike} className="activity-statItem-blank-heart-icon" alt="heart" />
                                    ) : (
                                        <img src="/images/blank_heart.png" onClick={handleLike} className="activity-statItem-blank-heart-icon" alt="heart" />
                                    )}
                                    <div className="activity-like-comment-count">{likesCount || 0}</div>
                                </div>
                                <div className="activity-statItem">
                                    <img src="/images/chat.png" className="activity-statItem-blank-heart-icon" alt="chat" />
                                    <div className="activity-like-comment-count">{activityObj?.commentCount || 0}</div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ActivityCommentsPopup;
